import { useCallback, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "src/primitives/DropdownMenu";

import { ALIGN_MAP, SIDE_MAP } from "./constants";
import { SplitTrigger } from "./SplitTrigger";
import { TriggerButton } from "./TriggerButton";
import { DropdownContext, type DropdownProps } from "./types";

const DropdownBase = ({
  label,
  icon,
  buttonProps,
  customTarget,
  disabled = false,
  children,
  position = "bottom-end",
  closeOnEsc = true,
  closeOnSelect = true,
  closeOnOutsideClick = true,
  modal = false,
  isOpen: isOpenProp,
  onClose,
  onClick,
  className,
  triggerRef,
  dropdownProps,
}: DropdownProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isOpenProp !== undefined;
  const open = isControlled ? isOpenProp : internalOpen;
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isControlled) setInternalOpen(isOpen);
      if (!isOpen) onClose?.();
    },
    [isControlled, onClose]
  );

  const side = SIDE_MAP[position] ?? "bottom";
  const align = ALIGN_MAP[position] ?? "end";

  // Split-button mode: onClick provided without a customTarget.
  const isSplit = !!onClick && !customTarget;

  const sharedTriggerProps = {
    label,
    icon,
    buttonProps,
    disabled,
    isOpen: open,
    onClick,
  };

  const triggerNode = isSplit ? (
    <SplitTrigger
      {...sharedTriggerProps}
      actionButtonRef={triggerRef}
      className={className}
    />
  ) : (
    <DropdownMenuTrigger asChild disabled={disabled} className={className}>
      {customTarget ? (
        typeof customTarget === "function" ? (
          customTarget({ isOpen: open })
        ) : (
          customTarget
        )
      ) : (
        <TriggerButton ref={triggerRef} {...sharedTriggerProps} />
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownContext.Provider value={{ closeOnSelect }}>
      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={modal}>
        {triggerNode}
        <DropdownMenuContent
          side={side}
          align={align}
          onEscapeKeyDown={closeOnEsc ? undefined : e => e.preventDefault()}
          onInteractOutside={
            closeOnOutsideClick ? undefined : e => e.preventDefault()
          }
          {...dropdownProps}
        >
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </DropdownContext.Provider>
  );
};

export { DropdownBase };
